import { useEffect, useRef, useState } from "react";
import { PaymentService } from "@propertelligent/client-api";
import apiHelper from "../utils/apiHelper";
import { TransferFailureReason } from "../utils/enumHelpers";

// TODO(feature/plaid-pay-status-ux): transferFailureReason and autoPayTransferPending are new
// backend fields (feature/plaid-pay-status-ux-backend / fix/expose-autopay-pending-vm) not yet
// published in @propertelligent/client-api, so this hook reads them off a local row shape.
// TransferFailureReason is the interim numeric mirror of the backend enum and is declared in
// utils/enumHelpers alongside its label lookup -- see there for why the values are numeric.
// Once the client republishes, drop PollablePaymentDueAccountVM below and read the fields off
// the generated PaymentDueAccountVM -- same interim-local-type pattern already used for
// transferFailed in PaymentPayablesORReceivablesList.tsx.
type PollablePaymentDueAccountVM = {
  id?: number;
  autoPayTransferPending?: boolean;
  transferFailed?: boolean;
  transferFailureReason?: TransferFailureReason;
};

export interface PaymentConfirmationResult {
  failed: boolean;
  failureReason?: TransferFailureReason;
}

export type PaymentConfirmationPollStatus =
  | "idle"
  | "polling"
  | "resolved"
  | "timeout";

export interface PaymentConfirmationPollState {
  status: PaymentConfirmationPollStatus;
  results: Record<number, PaymentConfirmationResult>;
}

export interface PaymentFailureItem {
  id: number;
  reasonLabel: string;
}

/**
 * Multiple underlying PaymentDueAccountIds can belong to the same visible grouped
 * payables/receivables row (see collectSettlePayGroupIds in paymentDueAccount.service.ts) and
 * fail independently for the identical reason -- rendering one PaymentStatusCard tag per id
 * would then show the same sentence twice for what the user perceives as one payment. Collapse
 * to one entry per unique reason; the kept `id` is only used as the StatusTag list key.
 */
export const dedupeFailedResults = (
  items: PaymentFailureItem[],
): PaymentFailureItem[] => {
  const seenReasons = new Set<string>();
  const deduped: PaymentFailureItem[] = [];
  for (const item of items) {
    if (seenReasons.has(item.reasonLabel)) continue;
    seenReasons.add(item.reasonLabel);
    deduped.push(item);
  }
  return deduped;
};

const POLL_INTERVAL_MS = 2500;
// Bounded window: the reserve-then-confirm chain normally resolves in low single-digit
// seconds (synchronous Hangfire-pickup-plus-Plaid-HTTP-calls) -- this cap only absorbs queue
// latency under load, not a genuinely long external wait. On timeout, callers fall back to
// today's "submitted, check back later" messaging rather than waiting indefinitely.
const POLL_TIMEOUT_MS = 40000;

/**
 * Polls each PaymentDueAccount id in `ids` via GET /api/PaymentDueAccount/{id} every
 * ~2.5s until it resolves (`autoPayTransferPending` clears -- `transferFailed` then tells
 * success from failure), or the ~40s window elapses.
 *
 * Resolution is driven entirely by `autoPayTransferPending`, not by diffing
 * `paymentDueAccountStatusId` against a pre-submit baseline. A grouped row's hydration takes
 * only its FIRST member's status, so a baseline diff could false-resolve (or never resolve) a
 * sibling line whose own status didn't move the same way -- `autoPayTransferPending` is
 * per-due and always accurate regardless of grouping.
 *
 * `ids` is read by *value* -- the effect only restarts when the set of ids actually changes (a
 * joined-ids string is used as the effect key instead of the array reference itself, so a
 * caller re-rendering with a fresh-but-equal array literal each render does not restart
 * polling or leak an extra interval).
 */
export function usePaymentConfirmationPoll(
  ids: number[],
): PaymentConfirmationPollState {
  const [status, setStatus] = useState<PaymentConfirmationPollStatus>("idle");
  const [results, setResults] = useState<Record<number, PaymentConfirmationResult>>({});

  const idsKey = ids.join(",");
  // Kept in a ref (not an effect dep) purely so the interval/timeout closures below always see
  // the latest ids without needing idsKey's effect to be torn down and rebuilt on every caller
  // re-render -- the effect itself still only (re)starts when idsKey changes.
  const idsRef = useRef<number[]>(ids);
  idsRef.current = ids;

  useEffect(() => {
    if (!idsRef.current.length) {
      setStatus("idle");
      setResults({});
      return;
    }

    setResults({});
    setStatus("polling");

    let cancelled = false;
    const resolvedIds = new Set<number>();
    const targetIds = idsRef.current;

    const pollOnce = async () => {
      const pendingIds = targetIds.filter((id) => !resolvedIds.has(id));
      if (pendingIds.length === 0 || cancelled) {
        return;
      }

      await Promise.all(
        pendingIds.map(async (id) => {
          const response = await apiHelper(
            PaymentService.getApiPaymentDueAccount,
            { showNotification: false },
            id,
          );
          if (cancelled) {
            return;
          }

          const detail = response?.data?.success as
            | PollablePaymentDueAccountVM
            | undefined;
          // A transient fetch failure (no `success` payload) must not resolve the id --
          // keep it pending and let the next tick (or the timeout) decide.
          if (!detail) {
            return;
          }

          const transferFailed = detail.transferFailed === true;
          // "Still pending" = autoPayTransferPending === true; "resolved" = false. This is the
          // same reserve-then-confirm flag the backend flips synchronously once
          // TransferMoneyEventConsumer finishes (success or failure) -- no status-diffing or
          // baseline needed, and it is correct per-due even inside a mixed-status grouped row.
          const resolved = detail.autoPayTransferPending !== true;

          if (resolved) {
            resolvedIds.add(id);
            setResults((prev) => ({
              ...prev,
              [id]: {
                failed: transferFailed,
                failureReason: transferFailed
                  ? detail.transferFailureReason
                  : undefined,
              },
            }));
          }
        }),
      );

      if (!cancelled && resolvedIds.size === targetIds.length) {
        // Every id resolved -- stop both timers now instead of leaving the interval ticking
        // (as harmless no-op pollOnce calls) until the timeout eventually clears it.
        clearInterval(intervalId);
        clearTimeout(timeoutId);
        setStatus("resolved");
      }
    };

    // Fire immediately -- don't make the caller wait a full interval for the first check.
    pollOnce();
    const intervalId = setInterval(pollOnce, POLL_INTERVAL_MS);

    const timeoutId = setTimeout(() => {
      if (cancelled) {
        return;
      }
      clearInterval(intervalId);
      setStatus((current) => (current === "polling" ? "timeout" : current));
    }, POLL_TIMEOUT_MS);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally keyed on the
    // joined id list (idsKey), not the ids array reference itself; see the doc comment above.
  }, [idsKey]);

  return { status, results };
}

export default usePaymentConfirmationPoll;
